import { HASURA_CLAIMS_KEY } from "constants/auth";

function parseInviteTokenPayload(
  inviteToken: string
): Record<string, unknown> | null {
  try {
    const payloadSegment = inviteToken.split(".")[1];
    if (!payloadSegment) {
      return null;
    }
    const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

function getHasuraClaimsFromInviteToken(
  inviteToken: string
): Record<string, string> | null {
  const decoded = parseInviteTokenPayload(inviteToken);
  if (!decoded) {
    return null;
  }

  const claimsValue = decoded[HASURA_CLAIMS_KEY];
  if (!claimsValue) {
    return null;
  }

  if (typeof claimsValue === "object") {
    return claimsValue as Record<string, string>;
  }

  if (typeof claimsValue === "string") {
    try {
      return JSON.parse(claimsValue);
    } catch {
      return null;
    }
  }

  return null;
}

export function extractProjectIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/project\/([^/]+)/);
  return match?.[1] || null;
}

export function buildProjectDashboardInviteRedirectUrl(
  token: string,
  projectId: string
): string {
  return `/project/${projectId}/dashboard?projectId=${encodeURIComponent(projectId)}&token=${encodeURIComponent(token)}`;
}

function appendInviteParamsToRedirectUrl(
  redirectUrl: string,
  token: string,
  projectId: string
): string {
  if (redirectUrl.includes("token=")) {
    return redirectUrl;
  }

  const separator = redirectUrl.includes("?") ? "&" : "?";
  if (isProjectPathRedirect(redirectUrl)) {
    return `${redirectUrl}${separator}projectId=${encodeURIComponent(projectId)}&token=${encodeURIComponent(token)}`;
  }
  return `${redirectUrl}${separator}token=${encodeURIComponent(token)}&projectId=${encodeURIComponent(projectId)}`;
}

function isProjectPathRedirect(redirectUrl: string): boolean {
  return redirectUrl.startsWith("/project/");
}

export function buildProjectInviteRedirectUrl(
  token: string,
  projectId: string,
  existingRedirectUrl?: string | null
): string {
  if (existingRedirectUrl && isProjectPathRedirect(existingRedirectUrl)) {
    return appendInviteParamsToRedirectUrl(
      existingRedirectUrl,
      token,
      projectId
    );
  }

  return buildProjectDashboardInviteRedirectUrl(token, projectId);
}

export function getInviteTokenFromRedirect(
  redirectUrl: string | null
): string | null {
  if (!redirectUrl) {
    return null;
  }

  const queryStart = redirectUrl.indexOf("?");
  if (queryStart < 0) {
    return null;
  }

  return new URLSearchParams(redirectUrl.slice(queryStart)).get("token");
}

export function getProjectInviteSubscriptionId(
  inviteToken: string
): string | null {
  const hasuraClaims = getHasuraClaimsFromInviteToken(inviteToken);
  return hasuraClaims?.["x-hasura-subscription-id"] || null;
}

export function isProjectInviteRedirectUrl(
  redirectUrl: string | null
): boolean {
  if (!redirectUrl) {
    return false;
  }

  const inviteToken = getInviteTokenFromRedirect(redirectUrl);
  if (!inviteToken || !getProjectInviteSubscriptionId(inviteToken)) {
    return false;
  }

  const path = redirectUrl.split("?")[0];
  const queryStart = redirectUrl.indexOf("?");
  return (
    isProjectPathRedirect(path) ||
    (queryStart >= 0 &&
      Boolean(
        new URLSearchParams(redirectUrl.slice(queryStart)).get("projectId")
      ))
  );
}

export function getProjectInviteRedirectUrlFromParams(
  search: string,
  hash = ""
): string | null {
  const params = new URLSearchParams(search);
  let redirectUrl = params.get("redirectUrl");
  const token = params.get("token");
  const projectId = params.get("projectId");

  if (!redirectUrl && token && projectId) {
    redirectUrl = buildProjectInviteRedirectUrl(token, projectId);
  } else if (
    redirectUrl &&
    token &&
    projectId &&
    !redirectUrl.includes("token=")
  ) {
    redirectUrl = buildProjectInviteRedirectUrl(token, projectId, redirectUrl);
  }

  if (redirectUrl && isProjectInviteRedirectUrl(redirectUrl)) {
    return redirectUrl;
  }

  if (!hash) {
    return null;
  }

  const hashValue = hash.startsWith("#") ? hash.slice(1) : hash;
  const hashParams = new URLSearchParams(
    hashValue.startsWith("?") ? hashValue : `?${hashValue}`
  );
  const hashToken = hashParams.get("token");
  const hashProjectId = hashParams.get("projectId");

  if (hashToken && hashProjectId) {
    const hashRedirect = buildProjectInviteRedirectUrl(
      hashToken,
      hashProjectId
    );
    if (isProjectInviteRedirectUrl(hashRedirect)) {
      return hashRedirect;
    }
  }

  return null;
}

export function getGenericPostLoginRedirectUrl(search: string): string | null {
  const redirectUrl = new URLSearchParams(search).get("redirectUrl");
  if (!redirectUrl || isProjectInviteRedirectUrl(redirectUrl)) {
    return null;
  }
  return redirectUrl;
}

export function getPostLoginRedirectUrl(
  search: string,
  hash = ""
): string | null {
  return (
    getProjectInviteRedirectUrlFromParams(search, hash) ||
    getGenericPostLoginRedirectUrl(search)
  );
}

/** True when the URL or persisted SSO state carries a post-login destination. */
export function hasPendingPostLoginRedirect(
  search: string,
  hash = ""
): boolean {
  if (
    getProjectInviteRedirectUrlFromParams(search, hash) ||
    getGenericPostLoginRedirectUrl(search)
  ) {
    return true;
  }

  try {
    return Boolean(sessionStorage.getItem(SSO_POST_LOGIN_REDIRECT_KEY));
  } catch {
    return false;
  }
}

export function getProjectInviteSubscriptionIdFromRedirect(
  redirectUrl: string | null
): string | null {
  const inviteToken = getInviteTokenFromRedirect(redirectUrl);
  if (!inviteToken) {
    return null;
  }

  return getProjectInviteSubscriptionId(inviteToken);
}

export function resolveSubscriptionForProjectInvite(
  redirectUrl: string | null,
  subscriptions: Array<{ id: string }>
): string | null {
  const inviteSubscriptionId =
    getProjectInviteSubscriptionIdFromRedirect(redirectUrl);
  if (
    inviteSubscriptionId &&
    subscriptions.some((sub) => sub.id === inviteSubscriptionId)
  ) {
    return inviteSubscriptionId;
  }
  return null;
}

export function getProjectIdFromInviteRedirect(
  redirectUrl: string | null
): string | null {
  if (!redirectUrl) {
    return null;
  }

  const fromPath = extractProjectIdFromPathname(redirectUrl.split("?")[0]);
  if (fromPath) {
    return fromPath;
  }

  const queryStart = redirectUrl.indexOf("?");
  if (queryStart < 0) {
    return null;
  }

  const projectId = new URLSearchParams(redirectUrl.slice(queryStart)).get(
    "projectId"
  );
  return projectId?.trim() || null;
}

export function resolveInvitePostLoginNavigationUrl(
  postLoginRedirectUrl: string | null,
  projectInviteRedirectUrl: string | null,
  defaultUrl = "/projects"
): string {
  if (postLoginRedirectUrl) {
    return postLoginRedirectUrl;
  }

  const projectId = getProjectIdFromInviteRedirect(projectInviteRedirectUrl);
  if (projectId) {
    return `/project/${projectId}/dashboard`;
  }

  return defaultUrl;
}

const PROJECT_DASHBOARD_PATH = /^\/project\/([^/]+)\/dashboard\/?$/;
const PROJECT_DASHBOARD_TRANSIENT_PARAM_KEYS = [
  "token",
  "projectId",
  "data",
  "sdata",
  "reserved"
] as const;

function parseHashParams(hash: string): URLSearchParams {
  if (!hash.trim()) {
    return new URLSearchParams();
  }
  const hashValue = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(
    hashValue.startsWith("?") ? hashValue.slice(1) : hashValue
  );
}

function serializeHashParams(params: URLSearchParams): string {
  const serialized = params.toString();
  return serialized ? `#?${serialized}` : "";
}

function removeTransientParamsFromSearchParams(
  params: URLSearchParams
): boolean {
  let removed = false;
  for (const key of PROJECT_DASHBOARD_TRANSIENT_PARAM_KEYS) {
    if (params.has(key)) {
      params.delete(key);
      removed = true;
    }
  }
  return removed;
}

export function stripProjectDashboardTransientParams(
  pathname: string,
  search: string,
  hash = ""
): { pathname: string; search: string; hash: string } | null {
  const match = pathname.match(PROJECT_DASHBOARD_PATH);
  if (!match) {
    return null;
  }

  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const hashParams = parseHashParams(hash);
  const removedFromSearch = removeTransientParamsFromSearchParams(params);
  const removedFromHash = removeTransientParamsFromSearchParams(hashParams);

  if (!removedFromSearch && !removedFromHash) {
    return null;
  }

  const remainingSearch = params.toString();
  return {
    pathname: `/project/${match[1]}/dashboard`,
    search: remainingSearch ? `?${remainingSearch}` : "",
    hash: serializeHashParams(hashParams)
  };
}

/** Persists post-login URL across SSO IdP redirect (full page navigation). */
export const SSO_POST_LOGIN_REDIRECT_KEY = "ciq_sso_post_login_redirect";

export function persistSsoPostLoginRedirect(redirectUrl: string): void {
  if (!redirectUrl) {
    return;
  }
  sessionStorage.setItem(SSO_POST_LOGIN_REDIRECT_KEY, redirectUrl);
}

export function consumeSsoPostLoginRedirect(): string | null {
  const redirectUrl = sessionStorage.getItem(SSO_POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(SSO_POST_LOGIN_REDIRECT_KEY);
  return redirectUrl;
}

export function getPostLoginRedirectFromParams(
  search: string,
  hash: string,
  consumePersistedSsoRedirect: boolean
): string | null {
  const persistedSsoRedirect = consumePersistedSsoRedirect
    ? consumeSsoPostLoginRedirect()
    : sessionStorage.getItem(SSO_POST_LOGIN_REDIRECT_KEY);

  return (
    persistedSsoRedirect ||
    getProjectInviteRedirectUrlFromParams(search, hash) ||
    getGenericPostLoginRedirectUrl(search)
  );
}

export function buildSubscriptionSelectRedirect(
  postLoginRedirectUrl: string | null
) {
  if (!postLoginRedirectUrl) {
    return "/subscription-select";
  }
  return `/subscription-select?redirectUrl=${encodeURIComponent(postLoginRedirectUrl)}`;
}
